import { extractSessionData } from '../helpers/localStorage';
import { Client, PrivateKey } from 'dsteem';
import { getSteemConfig } from '../config';
import { backend } from './db-service';

const client = new Client(getSteemConfig());

export let SCHEDULE_ACCOUNT = 'steem-seven';

export async function fetchScheduleAccount(): Promise<string> {
    try {
        const response = await fetch(`${backend}/api/v1/schedule_account`);
        const data = await response.json();
        if (data.account) {
            SCHEDULE_ACCOUNT = data.account;
            return data.account;
        }
    } catch (e) {
        console.error('Error fetching mother account', e);
    }
    return SCHEDULE_ACCOUNT;
}

/**
 * Checks if a mother account is authorized in the user's posting authority.
 * @param username The username to check.
 * @param authorizedAccount The account to check for (defaults to fetched mother account).
 */
export async function isAuthorized(username: string, authorizedAccount?: string): Promise<boolean> {
    if (!username) return false;
    if (!authorizedAccount) authorizedAccount = await fetchScheduleAccount();
    const target = authorizedAccount.replace('@', '').trim().toLowerCase();
    const user = username.replace('@', '').trim().toLowerCase();
    
    try {
        const accounts = await client.database.getAccounts([user]);
        if (accounts && accounts.length > 0) {
            const account = accounts[0];
            const threshold = account.posting.weight_threshold || 1;
            const postingAuths = account.posting.account_auths || [];
            
            const auth = postingAuths.find((a: any) => a[0].toLowerCase() === target);
            return !!(auth && auth[1] >= threshold);
        }
        return false;
    } catch (error) {
        return false;
    }
}

/**
 * Authorizes an account to the user's posting authority.
 * Requires Active Key.
 * @param autologin Session data from localStorage.
 * @param authorizedAccount Account to authorize.
 * @param activeWif Optional active WIF if not available in autologin.
 */
export async function authorizeAccount(autologin: any, authorizedAccount?: string, activeWif: string = ""): Promise<{ success: boolean; message: string }> {
    if (!authorizedAccount) authorizedAccount = await fetchScheduleAccount();
    try {
        let data = extractSessionData(autologin);
        let username = data[0];
        let wif = activeWif || data[1];

        const accounts = await client.database.getAccounts([username]);
        if (!accounts || accounts.length === 0) {
            return { success: false, message: "Account not found." };
        }

        const account = accounts[0];
        
        // Check if already authorized
        const isAlreadyAuthorized = account.posting.account_auths.some((auth: any) => auth[0] === authorizedAccount);
        if (isAlreadyAuthorized) {
            return { success: true, message: "Account is already authorized." };
        }

        // Prepare new posting authority
        const newPosting = { ...account.posting };
        newPosting.account_auths.push([authorizedAccount, 1]);
        
        // Re-sort account_auths (required by Steem protocol)
        newPosting.account_auths.sort((a, b) => a[0].localeCompare(b[0]));

        const operation: any = [
            'account_update',
            {
                account: username,
                posting: newPosting,
                memo_key: account.memo_key,
                json_metadata: account.json_metadata
            }
        ];

        const key = PrivateKey.fromString(wif);
        const result = await client.broadcast.sendOperations([operation], key);
        
        return { success: true, message: "Authority granted successfully! TX ID: " + result.id };
    } catch (error: any) {
        return { success: false, message: error?.message || String(error) };
    }
}

/**
 * Broadcasts a comment using the mother account's authority.
 * This is a test function that assumes the caller has the mother account's posting key.
 * In a real scenario, this would happen on the backend.
 */
export async function testAuthorizedComment(
    user: string,
    motherPostingWif: string,
    parentAuthor: string,
    parentPermlink: string,
    body: string
): Promise<{ success: boolean; message: string }> {
    try {
        const permlink = 'test-authority-' + Math.random().toString(36).substring(2, 10);
        const key = PrivateKey.fromString(motherPostingWif);

        const operation: any = [
            'comment',
            {
                parent_author: parentAuthor,
                parent_permlink: parentPermlink,
                author: user,
                permlink: permlink,
                title: '',
                body: body,
                json_metadata: JSON.stringify({ app: 'speem/1.1', test: true })
            }
        ];

        console.log('Broadcasting operation:', JSON.stringify(operation, null, 2));
        const result = await client.broadcast.sendOperations([operation], key);
        return { success: true, message: "Comment posted with authority! TX ID: " + result.id };
    } catch (error: any) {
        console.error('Broadcast error:', error);
        return { success: false, message: error?.message || String(error) };
    }
}

/**
 * Revokes an account from the user's posting authority.
 * Requires Active Key.
 * @param autologin Session data from localStorage.
 * @param accountToRevoke Account to revoke.
 * @param activeWif Optional active WIF if not available in autologin.
 */
export async function revokeAccount(autologin: any, accountToRevoke?: string, activeWif: string = ""): Promise<{ success: boolean; message: string }> {
    if (!accountToRevoke) accountToRevoke = await fetchScheduleAccount();
    try {
        let data = extractSessionData(autologin);
        let username = data[0];
        let wif = activeWif || data[1];

        const accounts = await client.database.getAccounts([username]);
        if (!accounts || accounts.length === 0) {
            return { success: false, message: "Account not found." };
        }

        const account = accounts[0];
        
        // Check if authorized
        const target = accountToRevoke.replace('@', '').trim().toLowerCase();
        const isAuthorized = account.posting.account_auths.some((auth: any) => auth[0].toLowerCase() === target);
        if (!isAuthorized) {
            return { success: true, message: "Account is not authorized." };
        }

        // Prepare new posting authority (remove the account)
        const newPosting = { ...account.posting };
        newPosting.account_auths = newPosting.account_auths.filter((auth: any) => auth[0].toLowerCase() !== target);
        
        const operation: any = [
            'account_update',
            {
                account: username,
                posting: newPosting,
                memo_key: account.memo_key,
                json_metadata: account.json_metadata
            }
        ];

        const key = PrivateKey.fromString(wif);
        const result = await client.broadcast.sendOperations([operation], key);
        
        return { success: true, message: "Authority revoked successfully! TX ID: " + result.id };
    } catch (error: any) {
        return { success: false, message: error?.message || String(error) };
    }
}
